import { Tag } from '@nimbus-ds/components';
import { MenuButtonBaseProps } from '@nimbus-ds/menubutton';
import { MenuButton } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ChatDotsIcon } from '@tiendanube/icons';
import ShowByRole from 'App/components/ShowByRole';
import {
  NUVEM_CHAT_BILLING_DISABLED,
  NUVEM_CHAT_ONBOARDING_FINISHED,
} from 'App/featuresFlags';
import { useHandleMenuClick } from 'App/hooks';
import { trackingMenuClick } from 'App/tracking';
import { useResponsive } from 'commons/components';
import { NUVEMCHAT_MENU_CLICK } from 'config/trackingEvents';
import { useGetTags } from 'domains/Auth/hooks';
import { useNuvemApps } from 'domains/PartnersApps/pages/NuvemAppPage/hooks';

const path = {
  chat: '/chat',
  conversations: '/chat#/conversations',
  statistics: '/chat#/statistics',
  costs: '/chat#/costs',
  settings: '/chat#/configurations',
};
const pathHash = {
  chat: '#',
  conversations: '#/conversations',
  statistics: '#/statistics',
  costs: '#/costs',
  settings: '#/configurations',
};

function NuvemChatMenu() {
  const { t } = useTranslation('common');
  const { hash, pathname } = useLocation();
  const { handleClickItem, handleClickAccordion } = useHandleMenuClick();
  const { nuvemApps, isEmpty } = useNuvemApps();
  const { isMobile } = useResponsive();
  const tags = useGetTags();
  const hasNuvemChatBillingDisabled = tags.includes(
    NUVEM_CHAT_BILLING_DISABLED,
  );
  const hasNuvemChatOnboardingFinished = tags.includes(
    NUVEM_CHAT_ONBOARDING_FINISHED,
  );
  const handleNuvemChatClick = () => {
    trackingMenuClick({
      tag: NUVEMCHAT_MENU_CLICK,
      options: {
        is_mobile_device: isMobile.toString(),
        is_nuvem_chat_installed: nuvemApps?.nuvemchat ? 'true' : 'false',
      },
    });
    nuvemApps?.nuvemchat ? handleClickAccordion() : handleClickItem();
  };

  const allItems = [
    {
      label: t('menu.manage.nuvemchat.conversations'),
      onClick: handleClickItem,
      active: pathname === path.chat && hash.startsWith(pathHash.conversations),
      href: path.conversations,
    },
    {
      label: t('menu.manage.nuvemchat.statistics'),
      onClick: handleClickItem,
      active: pathname === path.chat && hash.startsWith(pathHash.statistics),
      href: path.statistics,
    },
    {
      label: t('menu.manage.nuvemchat.costs'),
      onClick: handleClickItem,
      active: pathname === path.chat && hash.startsWith(pathHash.costs),
      href: path.costs,
    },
    {
      label: t('menu.manage.nuvemchat.settings'),
      onClick: handleClickItem,
      active: pathname === path.chat && hash.startsWith(pathHash.settings),
      href: path.settings,
    },
  ];
  //if the user has nuvemchat and the onboarding is finished, show the items
  const itemsChat: (MenuButtonBaseProps & {
    href: string;
  })[] =
    nuvemApps?.nuvemchat && hasNuvemChatOnboardingFinished
      ? allItems.filter(
          (item) => !(hasNuvemChatBillingDisabled && item.href === path.costs),
        )
      : [];

  const isChatPath = (pathname: string, hash: string) => {
    const isBaseChatPath = pathname === path.chat;
    const isChatHash = Object.values(pathHash).some((chatHash) =>
      hash.startsWith(chatHash),
    );
    return isBaseChatPath && (hash === '' || isChatHash);
  };

  const activeChat = isChatPath(pathname, hash);

  const containerMenuButtonProps = {
    id: 'control-chat',
    label: t('menu.manage.nuvemchat.title'),
    startIcon: ChatDotsIcon,
    'aria-controls': 'content-chat',
    onClick: handleNuvemChatClick,
    children: <Tag appearance="primary">{t('menu.new')}</Tag>,
  };

  return (
    <ShowByRole includeRoles={['nuvem_chat']}>
      {!isEmpty &&
        (itemsChat.length > 0 ? (
          <MenuButton.Accordion
            contentid="content-chat"
            menuButton={containerMenuButtonProps}
            open={activeChat}
            active={activeChat}
            as={Link}
            to={path.chat}
          >
            {itemsChat.map((item, index) => (
              <MenuButton
                key={index}
                as={Link}
                label={item.label}
                onClick={item.onClick}
                to={item.href}
                active={item.active}
              />
            ))}
          </MenuButton.Accordion>
        ) : (
          <MenuButton
            {...containerMenuButtonProps}
            as={Link}
            to={path.chat}
            active={activeChat}
          />
        ))}
    </ShowByRole>
  );
}

export default NuvemChatMenu;
